import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconActivity,
  IconAdjustments,
  IconBan,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconCoin,
  IconCopy,
  IconDeviceFloppy,
  IconDownload,
  IconExternalLink,
  IconInfoCircle,
  IconPlus,
  IconRocket,
  IconRoute,
  IconSparkles,
  IconTargetArrow,
  IconTrash,
  IconTrendingUp,
  IconWallet,
  IconX,
  type TablerIcon,
} from '@tabler/icons-react';
import {
  BalanceFilters,
  ROBINHOOD_SCALAR_FILTER_KEYS,
  RobinhoodExportCreated,
  RobinhoodExportTask,
  RobinhoodFilters,
  RobinhoodMetricMap,
  RobinhoodPeriod,
  RobinhoodPreset,
  RobinhoodPresetPayload,
  RobinhoodScalarFilterKey,
  RobinhoodWinrateBucket,
  robinhoodPresetsClient,
} from '../../api/robinhoodPresetsClient';
import BalanceFiltersControl, {
  apiBalanceToDecimal,
  BUILT_IN_BALANCE_PRECISION,
  compareDecimalBalances,
  compareRawAmounts,
  decimalBalanceToApi,
  isDecimalBalance,
  isRawUInt256,
} from './BalanceFiltersControl';
import './robinhoodPresets.css';

type RangeDefinition = {
  label: string;
  min: RobinhoodScalarFilterKey;
  max: RobinhoodScalarFilterKey;
  unit?: string;
  help: string;
  step?: number | 'any';
  minimum?: number;
  suggestions?: Array<{ label: string; min: number | null; max: number | null }>;
};

type FilterSectionDefinition = {
  id: string;
  title: string;
  description: string;
  emoji: string;
  icon: TablerIcon;
  ranges: RangeDefinition[];
};

const PERFORMANCE_RANGES: RangeDefinition[] = [
  {
    label: 'Wallet ROI', min: 'min_roi', max: 'max_roi', unit: '%',
    help: 'Overall lifetime return across tokens with at least one sell.',
    suggestions: [
      { label: 'Positive', min: 0, max: null },
      { label: '2×+', min: 100, max: null },
      { label: '0–500%', min: 0, max: 500 },
    ],
  },
  {
    label: 'PNL', min: 'min_pnl_usd', max: 'max_pnl_usd', unit: 'USD',
    help: 'Earned USD minus spent USD for sold or partially sold tokens.',
    suggestions: [
      { label: 'Profit only', min: 0, max: null },
      { label: '$100+', min: 100, max: null },
    ],
  },
];

const FILTER_SECTIONS: FilterSectionDefinition[] = [
  {
    id: 'token-edge',
    title: 'Token outcomes',
    description: 'Typical token result, less distorted by a single lucky trade.',
    emoji: '📈',
    icon: IconTrendingUp,
    ranges: [
      {
        label: 'Median token ROI', min: 'min_median_token_roi', max: 'max_median_token_roi', unit: '%',
        help: 'Middle ROI across closed tokens. Median is more resistant to outliers.',
        suggestions: [{ label: 'Positive', min: 0, max: null }, { label: '0–300%', min: 0, max: 300 }],
      },
      {
        label: 'Average token ROI', min: 'min_avg_token_roi', max: 'max_avg_token_roi', unit: '%',
        help: 'Arithmetic average ROI across tokens with at least one sell.',
      },
    ],
  },
  {
    id: 'mcap',
    title: 'Average / Median MCAP',
    description: 'Market cap at the first buy and the last sell across completed token trades.',
    emoji: '📊',
    icon: IconChartBar,
    ranges: [
      {
        label: 'Average First MCAP', min: 'min_avg_first_mcap_usd', max: 'max_avg_first_mcap_usd', unit: 'USD', minimum: 0,
        help: 'Average market cap at the first buy across tokens with a buy, a sell, supply and a valid first-buy price.',
      },
      {
        label: 'Average Last MCAP', min: 'min_avg_last_mcap_usd', max: 'max_avg_last_mcap_usd', unit: 'USD', minimum: 0,
        help: 'Average market cap at the last sell across tokens with a buy, a sell, supply and a valid last-sell price.',
      },
      {
        label: 'Median First MCAP', min: 'min_median_first_mcap_usd', max: 'max_median_first_mcap_usd', unit: 'USD', minimum: 0,
        help: 'Median market cap at the first buy. The median reduces the influence of unusually large or small tokens.',
      },
      {
        label: 'Median Last MCAP', min: 'min_median_last_mcap_usd', max: 'max_median_last_mcap_usd', unit: 'USD', minimum: 0,
        help: 'Median market cap at the last sell. Tokens without supply or the required price are skipped.',
      },
    ],
  },
  {
    id: 'activity',
    title: 'Trading activity',
    description: 'How broad and frequent the wallet activity is.',
    emoji: '⚡',
    icon: IconActivity,
    ranges: [
      {
        label: 'Traded tokens', min: 'min_total_traded_tokens', max: 'max_total_traded_tokens', unit: 'tokens', step: 1,
        help: 'All tokens active in the selected period, including tokens without sells.',
        suggestions: [{ label: '5+', min: 5, max: null }, { label: '10–100', min: 10, max: 100 }],
      },
      {
        label: 'Tokens with sells', min: 'min_tokens_with_sells_count', max: 'max_tokens_with_sells_count', unit: 'tokens', step: 1,
        help: 'Tokens where the wallet made at least one sell.',
      },
      {
        label: 'Tokens without sells', min: 'min_tokens_without_sells_count', max: 'max_tokens_without_sells_count', unit: 'tokens', step: 1,
        help: 'Tokens that were bought but never sold.',
      },
      {
        label: 'Unsold token share', min: 'min_tokens_without_sells_percent', max: 'max_tokens_without_sells_percent', unit: '%',
        help: 'Share of period tokens with no sells. A lower cap filters out abandoned positions.',
        suggestions: [{ label: '≤20%', min: null, max: 20 }, { label: '≤50%', min: null, max: 50 }],
      },
      {
        label: 'Total swaps', min: 'min_total_swaps_count', max: 'max_total_swaps_count', unit: 'swaps', step: 1,
        help: 'All buys and sells across period tokens.',
      },
      {
        label: 'Total buys', min: 'min_total_buys_count', max: 'max_total_buys_count', unit: 'buys', step: 1,
        help: 'Buy swaps across all period tokens.',
      },
      {
        label: 'Total sells', min: 'min_total_sells_count', max: 'max_total_sells_count', unit: 'sells', step: 1,
        help: 'Sell swaps across all period tokens.',
      },
    ],
  },
  {
    id: 'capital',
    title: 'Capital flow',
    description: 'USD volume split between closed and still-open positions.',
    emoji: '💰',
    icon: IconWallet,
    ranges: [
      { label: 'Spent on sold tokens', min: 'min_spent_usd', max: 'max_spent_usd', unit: 'USD', help: 'Purchases only for tokens that have at least one sell.' },
      { label: 'Earned from sells', min: 'min_earned_usd', max: 'max_earned_usd', unit: 'USD', help: 'USD value received from token sells.' },
      { label: 'Spent on all tokens', min: 'min_all_tokens_spent_usd', max: 'max_all_tokens_spent_usd', unit: 'USD', help: 'Purchases of every period token, whether sold or not.' },
      { label: 'Spent without sells', min: 'min_tokens_without_sells_spent_usd', max: 'max_tokens_without_sells_spent_usd', unit: 'USD', help: 'Capital put into tokens that have never been sold.' },
    ],
  },
  {
    id: 'positions',
    title: 'Position behavior',
    description: 'Entry cadence and how completely the wallet exits positions.',
    emoji: '🎯',
    icon: IconCoin,
    ranges: [
      { label: 'Average buys per token', min: 'min_avg_token_buys', max: 'max_avg_token_buys', help: 'Average buy count for tokens with at least one sell.' },
      { label: 'Average sells per token', min: 'min_avg_token_sells', max: 'max_avg_token_sells', help: 'Average sell count for tokens with at least one sell.' },
      { label: 'Average position sold', min: 'min_avg_avg_sell_percent', max: 'max_avg_avg_sell_percent', unit: '%', help: 'Average percentage of the bought amount that was sold.' },
      { label: 'Median position sold', min: 'min_median_avg_token_sell_percent', max: 'max_median_avg_token_sell_percent', unit: '%', help: 'Median sold percentage across tokens.' },
      { label: 'Average position held', min: 'min_avg_token_holding_percent', max: 'max_avg_token_holding_percent', unit: '%', help: 'Average remaining percentage of a position.' },
      { label: 'Not-closed positions', min: 'min_not_closed_positions_tokens_percent', max: 'max_not_closed_positions_tokens_percent', unit: '%', help: 'Share of tokens where sold amount is below bought amount.' },
    ],
  },
  {
    id: 'quality',
    title: 'Quality & routing',
    description: 'Data-quality guardrails and execution complexity.',
    emoji: '🛡️',
    icon: IconRoute,
    ranges: [
      {
        label: 'Sold before buy', min: 'min_sold_before_buy_tokens_percent', max: 'max_sold_before_buy_tokens_percent', unit: '%',
        help: 'Tokens where the first observed sell predates the first observed buy.',
        suggestions: [{ label: 'None', min: null, max: 0 }, { label: '≤5%', min: null, max: 5 }],
      },
      {
        label: 'Sold more than bought', min: 'min_sold_more_than_bought_tokens_percent', max: 'max_sold_more_than_bought_tokens_percent', unit: '%',
        help: 'Tokens where observed sold amount exceeds observed bought amount.',
        suggestions: [{ label: '≤10%', min: null, max: 10 }, { label: '≤20%', min: null, max: 20 }],
      },
      { label: 'Router swaps', min: 'min_router_swaps_count', max: 'max_router_swaps_count', unit: 'swaps', step: 1, help: 'Swaps routed through an aggregator or router.' },
      { label: 'Source events', min: 'min_source_events_count', max: 'max_source_events_count', unit: 'events', step: 1, help: 'Raw Swap events behind normalized swaps.' },
      { label: 'Multi-event swap tokens', min: 'min_multi_event_swaps_token_percent', max: 'max_multi_event_swaps_token_percent', unit: '%', help: 'Share of tokens with at least one multi-event swap.' },
    ],
  },
];

const DURATION_RANGES: RangeDefinition[] = [
  { label: 'Median trade duration', min: 'min_median_token_trade_duration_seconds', max: 'max_median_token_trade_duration_seconds', help: 'Median time from first buy to last sell.' },
  { label: 'Average trade duration', min: 'min_avg_token_trade_duration_seconds', max: 'max_avg_token_trade_duration_seconds', help: 'Average time from first buy to last sell.' },
  { label: 'Time to first sell', min: 'min_avg_token_seconds_first_sell_duration', max: 'max_avg_token_seconds_first_sell_duration', help: 'Average time from first buy to the first sell.' },
];

const SUMMARY_SORT_OPTIONS = [
  ['last_swap_block_timestamp', 'Last swap'], ['first_swap_block_timestamp', 'First swap'],
  ['winrate', 'Winrate'], ['roi', 'ROI'], ['pnl_usd', 'PNL'], ['spent_usd', 'Spent'],
  ['earned_usd', 'Earned'], ['all_tokens_spent_usd', 'All-token spent'],
  ['total_traded_tokens', 'Traded tokens'], ['tokens_with_sells_count', 'Tokens with sells'],
  ['tokens_without_sells_count', 'Tokens without sells'], ['total_buys_count', 'Buys'],
  ['total_sells_count', 'Sells'], ['total_swaps_count', 'Swaps'],
  ['median_token_roi', 'Median token ROI'], ['avg_token_roi', 'Average token ROI'],
  ['median_token_trade_duration_seconds', 'Median duration'], ['avg_token_trade_duration_seconds', 'Average duration'],
  ['router_swaps_count', 'Router swaps'], ['source_events_count', 'Source events'],
  ['rocket_x2_count', 'Rockets ×2'], ['rocket_x5_count', 'Rockets ×5'], ['rocket_x10_count', 'Rockets ×10'],
];

const TOKEN_SORT_OPTIONS = [
  ['last_swap_block_timestamp', 'Last swap'], ['first_swap_block_timestamp', 'First swap'],
  ['roi', 'ROI'], ['pnl_usd', 'PNL'], ['total_spent_usd', 'Spent'],
  ['total_earned_usd', 'Earned'], ['total_swaps_count', 'Swaps'], ['avg_sell_percent', 'Average sold %'],
  ['first_buy_token_price_usd', 'First-buy price'], ['last_sell_token_price_usd', 'Last-sell price'],
  ['weighted_avg_buy_token_price_usd', 'Weighted buy price'], ['weighted_avg_sell_token_price_usd', 'Weighted sell price'],
];

const PERIODS: RobinhoodPeriod[] = [1, 7, 14, 30, 45, 90];
const TERMINAL_EXPORT_STATUSES = new Set(['done', 'error', 'canceled']);

const clonePreset = (preset: RobinhoodPreset): RobinhoodPreset => JSON.parse(JSON.stringify(preset));

const normalizePreset = (preset: RobinhoodPreset): RobinhoodPreset => JSON.parse(JSON.stringify({
  ...preset,
  filters: {
    ...preset.filters,
    quote_tokens: preset.filters?.quote_tokens || {},
    projects: preset.filters?.projects || {},
    trade_providers: preset.filters?.trade_providers || {},
    weth_balance_min: apiBalanceToDecimal(preset.filters?.weth_balance_min),
    weth_balance_max: apiBalanceToDecimal(preset.filters?.weth_balance_max),
    eth_balance_min: apiBalanceToDecimal(preset.filters?.eth_balance_min),
    eth_balance_max: apiBalanceToDecimal(preset.filters?.eth_balance_max),
    total_eth_balance_min: apiBalanceToDecimal(preset.filters?.total_eth_balance_min),
    total_eth_balance_max: apiBalanceToDecimal(preset.filters?.total_eth_balance_max),
    usdg_balance_min: apiBalanceToDecimal(preset.filters?.usdg_balance_min),
    usdg_balance_max: apiBalanceToDecimal(preset.filters?.usdg_balance_max),
    custom_balance: (preset.filters?.custom_balance || []).map((item) => ({
      contract: item.contract.toLowerCase(),
      min: item.min ?? null,
      max: item.max ?? null,
    })),
    balance_mode_or: preset.filters?.balance_mode_or ?? true,
  },
}));

const toPayload = ({ id: _id, filters, ...payload }: RobinhoodPreset): RobinhoodPresetPayload => ({
  ...payload,
  filters: {
    ...Object.fromEntries(
      ROBINHOOD_SCALAR_FILTER_KEYS
        .filter((key) => filters[key] != null)
        .map((key) => [key, filters[key]]),
    ),
    weth_balance_min: decimalBalanceToApi(filters.weth_balance_min),
    weth_balance_max: decimalBalanceToApi(filters.weth_balance_max),
    eth_balance_min: decimalBalanceToApi(filters.eth_balance_min),
    eth_balance_max: decimalBalanceToApi(filters.eth_balance_max),
    total_eth_balance_min: decimalBalanceToApi(filters.total_eth_balance_min),
    total_eth_balance_max: decimalBalanceToApi(filters.total_eth_balance_max),
    usdg_balance_min: decimalBalanceToApi(filters.usdg_balance_min),
    usdg_balance_max: decimalBalanceToApi(filters.usdg_balance_max),
    quote_tokens: filters.quote_tokens || {},
    projects: filters.projects || {},
    trade_providers: filters.trade_providers || {},
    custom_balance: (filters.custom_balance || []).map((item) => ({
      contract: item.contract.toLowerCase() as `0x${string}`,
      min: item.min ?? null,
      max: item.max ?? null,
    })),
    balance_mode_or: filters.balance_mode_or ?? true,
  },
});
const readNumber = (value: string): number | null => value === '' ? null : Number(value);

const formatDuration = (seconds: number | null | undefined) => {
  if (seconds == null) return '';
  if (seconds === 0) return '0s';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${days ? `${days}d` : ''}${hours ? `${hours}h` : ''}${minutes ? `${minutes}m` : ''}${secs ? `${secs}s` : ''}` || '0s';
};

const parseDuration = (input: string): number | null => {
  const value = input.trim().toLowerCase();
  if (!value) return null;
  if (/^\d+(\.\d+)?$/.test(value)) return Number(value);
  const compact = value.replace(/\s/g, '');
  const pattern = /(\d+(?:\.\d+)?)(d|h|m|s)/g;
  let consumed = '';
  let total = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(compact)) !== null) {
    consumed += match[0];
    const multiplier = match[2] === 'd' ? 86400 : match[2] === 'h' ? 3600 : match[2] === 'm' ? 60 : 1;
    total += Number(match[1]) * multiplier;
  }
  return consumed && consumed === compact ? total : null;
};

const friendlyStatus = (status: string) => ({
  created: 'Queued', queued_release: 'Waiting for release', analyzing_wallets: 'Analyzing wallets',
  loading_tokens: 'Loading tokens', building_files: 'Building files', done: 'Delivered',
  error: 'Export failed', canceled: 'Canceled',
}[status] || status);

function HelpTip({ text, title = 'How it works' }: { text: string; title?: string }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <span className="rh-help" ref={rootRef}>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-label={`Help: ${title}`} aria-expanded={open}>
        <IconInfoCircle size={14} />
      </button>
      {open && (
        <span className="rh-help-popover" role="tooltip">
          <span className="rh-help-popover-head">
            <IconInfoCircle size={13} />
            <strong>{title}</strong>
            <button type="button" aria-label="Close help" onClick={() => setOpen(false)}><IconX size={12} /></button>
          </span>
          <span>{text}</span>
        </span>
      )}
    </span>
  );
}

function FormatPicker({ value, onChange }: { value: RobinhoodPreset['format_type']; onChange: (value: RobinhoodPreset['format_type']) => void }) {
  return (
    <div className="rh-format-picker" aria-label="Export format">
      {([
        ['EXCEL', 'Excel'], ['TXT_EXCEL', 'Excel + TXT'], ['TXT', 'TXT'],
      ] as Array<[RobinhoodPreset['format_type'], string]>).map(([format, label]) => (
        <button type="button" className={value === format ? 'active' : ''} key={format} onClick={() => onChange(format)}>{label}</button>
      ))}
    </div>
  );
}

function Toggle({ checked, onChange, label, detail }: { checked: boolean; onChange: (value: boolean) => void; label: string; detail?: string }) {
  return (
    <label className="rh-toggle-row">
      <span><strong>{label}</strong>{detail && <small>{detail}</small>}</span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.currentTarget.checked)} />
      <i aria-hidden="true"><b /></i>
    </label>
  );
}

function RangeField({ definition, filters, onChange }: {
  definition: RangeDefinition;
  filters: RobinhoodFilters;
  onChange: (key: RobinhoodScalarFilterKey, value: number | null) => void;
}) {
  const min = filters[definition.min] ?? null;
  const max = filters[definition.max] ?? null;
  const [showMax, setShowMax] = useState(max != null);
  const active = min != null || max != null;

  useEffect(() => { if (max != null) setShowMax(true); }, [max]);

  return (
    <div className={`rh-smart-field ${active ? 'active' : ''}`}>
      <div className="rh-smart-field-head">
        <div className="rh-field-label">
          <strong>{definition.label}</strong>
          {definition.unit && <span>{definition.unit}</span>}
          <HelpTip text={definition.help} title={definition.label} />
        </div>
        {active && (
          <button className="rh-clear-field" type="button" title="Disable this filter" onClick={() => {
            onChange(definition.min, null);
            onChange(definition.max, null);
            setShowMax(false);
          }}><IconBan size={14} /></button>
        )}
      </div>
      <div className="rh-threshold-inputs">
        <label>
          <span>FROM</span>
          <input
            type="number"
            min={definition.minimum}
            step={definition.step || 'any'}
            inputMode="decimal"
            aria-label={`${definition.label} minimum`}
            placeholder="No minimum"
            value={min ?? ''}
            onChange={(event) => onChange(definition.min, readNumber(event.currentTarget.value))}
          />
        </label>
        {showMax ? (
          <label>
            <span>TO</span>
            <input
              type="number"
              min={definition.minimum}
              step={definition.step || 'any'}
              inputMode="decimal"
              aria-label={`${definition.label} maximum`}
              placeholder="No maximum"
              value={max ?? ''}
              onChange={(event) => onChange(definition.max, readNumber(event.currentTarget.value))}
            />
          </label>
        ) : (
          <button className="rh-add-cap" type="button" onClick={() => setShowMax(true)}><IconPlus size={13} /> add upper limit</button>
        )}
      </div>
      {definition.suggestions && (
        <div className="rh-suggestion-row">
          {definition.suggestions.map((suggestion) => (
            <button type="button" key={suggestion.label} onClick={() => {
              onChange(definition.min, suggestion.min);
              onChange(definition.max, suggestion.max);
              setShowMax(suggestion.max != null);
            }}>{suggestion.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const compactNumber = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

function WinrateSpectrum({ buckets, loading, error, min, max, period }: {
  buckets: RobinhoodWinrateBucket[] | null;
  loading: boolean;
  error: string | null;
  min: number;
  max: number;
  period: RobinhoodPeriod;
}) {
  const [focused, setFocused] = useState<RobinhoodWinrateBucket | null>(null);
  const visibleBuckets = (buckets || []).filter((bucket) => bucket.winrate_percent > 0 && bucket.winrate_percent < 100);
  const bucketsByWinrate = new Map(visibleBuckets.map((bucket) => [bucket.winrate_percent, bucket]));
  const smoothingWeights = [1, 2, 3, 4, 3, 2, 1];
  const plotBuckets = Array.from({ length: 99 }, (_, index) => {
    const winrate = index + 1;
    let weightedCount = 0;
    let totalWeight = 0;
    smoothingWeights.forEach((weight, weightIndex) => {
      const neighbor = winrate + weightIndex - 3;
      if (neighbor < 1 || neighbor > 99) return;
      weightedCount += (bucketsByWinrate.get(neighbor)?.wallets_count || 0) * weight;
      totalWeight += weight;
    });
    return {
      bucket: bucketsByWinrate.get(winrate) || { winrate_percent: winrate, wallets_count: 0, percent: 0 },
      smoothedCount: totalWeight > 0 ? weightedCount / totalWeight : 0,
    };
  });
  const positiveTrend = plotBuckets.map((point) => point.smoothedCount).filter((count) => count > 0).sort((left, right) => left - right);
  const trendP92 = positiveTrend[Math.floor((positiveTrend.length - 1) * 0.92)] || 1;
  const visualCeiling = Math.max(1, trendP92 * 1.18);
  const chartPoints = plotBuckets.map((point, index) => {
    const visualRatio = Math.min(point.smoothedCount, visualCeiling) / visualCeiling;
    const height = visualRatio > 0 ? Math.max(1.2, visualRatio * 48) : 0;
    return { ...point, x: index + 0.5, y: 51 - height, height };
  });
  const trendLine = chartPoints.reduce((path, point, index, points) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = points[index - 1];
    const middle = (previous.x + point.x) / 2;
    return `${path} C ${middle} ${previous.y}, ${middle} ${point.y}, ${point.x} ${point.y}`;
  }, '');
  const trendArea = `${trendLine} L 98.5 51 L 0.5 51 Z`;
  const totalCount = visibleBuckets.reduce((total, bucket) => total + bucket.wallets_count, 0);
  const selectedCount = visibleBuckets
    .filter((bucket) => bucket.winrate_percent >= min && bucket.winrate_percent <= max)
    .reduce((total, bucket) => total + bucket.wallets_count, 0);
  const selectedPercent = totalCount > 0 ? (selectedCount / totalCount) * 100 : 0;

  useEffect(() => setFocused(null), [period]);

  return (
    <div className="rh-winrate-spectrum">
      <div className="rh-spectrum-head">
        <span><IconChartBar size={13} />{period}D WALLET DISTRIBUTION</span>
        {!loading && totalCount > 0 && <strong>{selectedPercent.toFixed(1)}% <small>in range</small></strong>}
      </div>
      <div className={`rh-spectrum-chart ${loading ? 'loading' : ''}`}>
        {loading ? (
          <div className="rh-spectrum-skeleton" aria-label="Loading winrate distribution">{Array.from({ length: 34 }, (_, index) => <i key={index} style={{ height: `${18 + ((index * 17) % 70)}%` }} />)}</div>
        ) : visibleBuckets.length > 0 ? (
          <svg key={period} viewBox="0 0 99 52" preserveAspectRatio="none" role="img" aria-label={`${period}-day winrate distribution`} onPointerLeave={(event) => { if (event.pointerType === 'mouse') setFocused(null); }}>
            <path className="rh-spectrum-area" d={trendArea} />
            {chartPoints.map((point) => {
              const { bucket } = point;
              const selected = bucket.winrate_percent >= min && bucket.winrate_percent <= max;
              return (
                <rect
                  className={selected ? 'selected' : ''}
                  key={bucket.winrate_percent}
                  x={bucket.winrate_percent - 0.92}
                  y={point.y}
                  width="0.84"
                  height={point.height}
                  rx="0.18"
                  onPointerEnter={() => setFocused(bucket)}
                  onPointerDown={() => setFocused(bucket)}
                >
                  <title>{bucket.winrate_percent}–{bucket.winrate_percent + 1}% · {bucket.wallets_count.toLocaleString()} wallets · {bucket.percent.toFixed(2)}%</title>
                </rect>
              );
            })}
          </svg>
        ) : (
          <div className="rh-spectrum-empty" title={error || undefined}>{error ? `No spectrum for ${period}d` : `No wallets in the ${period}d sample`}</div>
        )}
      </div>
      <div className="rh-spectrum-axis"><span>1%</span><span>25</span><span>50</span><span>75</span><span>99%</span></div>
      <div className="rh-spectrum-caption">
        {focused
          ? <strong>{focused.winrate_percent}–{focused.winrate_percent + 1}% <span>{focused.wallets_count.toLocaleString()} wallets · {focused.percent.toFixed(2)}%</span></strong>
          : error
            ? <span>{error.includes('one of') ? 'Spectrum is available for 7d and 30d' : 'Could not load this period'}</span>
            : <span>{period}d sample · smoothed 1–99%{totalCount > 0 && <> · {compactNumber.format(selectedCount)} of {compactNumber.format(totalCount)} selected</>}</span>}
      </div>
    </div>
  );
}

function WinrateControl({ filters, onChange, spectrum, spectrumLoading, spectrumError, period }: {
  filters: RobinhoodFilters;
  onChange: (key: RobinhoodScalarFilterKey, value: number | null) => void;
  spectrum: RobinhoodWinrateBucket[] | null;
  spectrumLoading: boolean;
  spectrumError: string | null;
  period: RobinhoodPeriod;
}) {
  const min = filters.min_winrate ?? 0;
  const max = filters.max_winrate ?? 100;
  const setMin = (value: number) => onChange('min_winrate', value <= 0 ? null : Math.min(value, max - 1));
  const setMax = (value: number) => onChange('max_winrate', value >= 100 ? null : Math.max(value, min + 1));

  return (
    <div className="rh-winrate-control">
      <div className="rh-winrate-head">
        <div className="rh-field-label"><IconTargetArrow size={17} /><strong>Winrate</strong><HelpTip title="Winrate" text="Share of profitable tokens among tokens with at least one sell. Percent values use 0–100, not 0–1." /></div>
        <div className="rh-winrate-value"><strong>{min}%</strong><span>to</span><strong>{max}%</strong></div>
      </div>
      <div className="rh-dual-range" style={{ '--range-start': `${min}%`, '--range-end': `${max}%` } as React.CSSProperties}>
        <div className="rh-range-track"><i /></div>
        <input aria-label="Minimum winrate" type="range" min="0" max="100" step="1" value={min} onChange={(event) => setMin(Number(event.currentTarget.value))} />
        <input aria-label="Maximum winrate" type="range" min="0" max="100" step="1" value={max} onChange={(event) => setMax(Number(event.currentTarget.value))} />
      </div>
      <div className="rh-scale-labels"><span>0</span><span>25</span><span>50</span><span>75</span><span>100%</span></div>
      <WinrateSpectrum buckets={spectrum} loading={spectrumLoading} error={spectrumError} min={min} max={max} period={period} />
      <div className="rh-suggestion-row rh-centered-suggestions">
        <button type="button" onClick={() => { onChange('min_winrate', 40); onChange('max_winrate', null); }}>40%+</button>
        <button type="button" onClick={() => { onChange('min_winrate', 50); onChange('max_winrate', 85); }}>50–85%</button>
        <button type="button" onClick={() => { onChange('min_winrate', 60); onChange('max_winrate', null); }}>60%+</button>
        <button type="button" onClick={() => { onChange('min_winrate', null); onChange('max_winrate', null); }}>Any</button>
      </div>
    </div>
  );
}

function DurationInput({ label, value, placeholder, onChange }: {
  label: string;
  value: number | null | undefined;
  placeholder: string;
  onChange: (value: number | null) => void;
}) {
  const [text, setText] = useState(formatDuration(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => setText(formatDuration(value)), [value]);

  const commit = () => {
    if (!text.trim()) {
      setInvalid(false);
      onChange(null);
      return;
    }
    const parsed = parseDuration(text);
    if (parsed == null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setText(formatDuration(parsed));
    onChange(parsed);
  };

  return (
    <label className={invalid ? 'invalid' : ''}>
      <span>{label}</span>
      <input value={text} placeholder={placeholder} onChange={(event) => setText(event.currentTarget.value)} onBlur={commit} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
    </label>
  );
}

function DurationWindow({ title, help, min, max, onMinChange, onMaxChange, onExample, presets }: {
  title: string;
  help: string;
  min: number | null | undefined;
  max: number | null | undefined;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
  onExample?: () => void;
  presets: Array<{ label: string; min: number | null; max: number | null }>;
}) {
  return (
    <div className={`rh-duration-window ${min != null || max != null ? 'active' : ''}`}>
      <div className="rh-smart-field-head">
        <div className="rh-field-label"><IconClock size={16} /><strong>{title}</strong><HelpTip title={title} text={help} /></div>
        {onExample && <button className="rh-example-link" type="button" onClick={onExample}>Show example <IconExternalLink size={12} /></button>}
      </div>
      <div className="rh-duration-inputs">
        <DurationInput label="AT LEAST" value={min} placeholder="e.g. 1h" onChange={onMinChange} />
        <div className="rh-duration-line"><i /></div>
        <DurationInput label="AT MOST" value={max} placeholder="e.g. 7d" onChange={onMaxChange} />
      </div>
      <div className="rh-suggestion-row">
        {presets.map((preset) => <button type="button" key={preset.label} onClick={() => { onMinChange(preset.min); onMaxChange(preset.max); }}>{preset.label}</button>)}
        <button type="button" onClick={() => { onMinChange(null); onMaxChange(null); }}>Clear</button>
      </div>
    </div>
  );
}

function RocketsControl({ filters, onChange }: {
  filters: RobinhoodFilters;
  onChange: (key: RobinhoodScalarFilterKey, value: number | null) => void;
}) {
  const rockets: Array<{ label: string; min: RobinhoodScalarFilterKey; max: RobinhoodScalarFilterKey; note: string }> = [
    { label: '×2', min: 'min_rocket_x2_count', max: 'max_rocket_x2_count', note: 'ROI ≥ 200%' },
    { label: '×5', min: 'min_rocket_x5_count', max: 'max_rocket_x5_count', note: 'ROI ≥ 500%' },
    { label: '×10', min: 'min_rocket_x10_count', max: 'max_rocket_x10_count', note: 'ROI ≥ 1000%' },
  ];
  return (
    <div className="rh-rockets-control">
      <div className="rh-field-label"><IconRocket size={17} /><strong>Rockets</strong><HelpTip title="Rockets" text="Count of sold tokens that reached the selected lifetime ROI threshold." /></div>
      <div className="rh-rocket-grid">
        {rockets.map((rocket) => (
          <div className="rh-rocket-cell" key={rocket.label}>
            <div><strong>{rocket.label}</strong><span>{rocket.note}</span></div>
            <label><span>MIN</span><input type="number" min="0" step="1" placeholder="Any" value={filters[rocket.min] ?? ''} onChange={(event) => onChange(rocket.min, readNumber(event.currentTarget.value))} /></label>
            <label><span>MAX</span><input type="number" min="0" step="1" placeholder="Any" value={filters[rocket.max] ?? ''} onChange={(event) => onChange(rocket.max, readNumber(event.currentTarget.value))} /></label>
          </div>
        ))}
      </div>
    </div>
  );
}

function TradeProvidersEditor({ value, onChange }: { value: RobinhoodMetricMap; onChange: (value: RobinhoodMetricMap) => void }) {
  const rows = Object.entries(value);
  const update = (index: number, key: string, range: { min: number | null; max: number | null }) => {
    onChange(Object.fromEntries(rows.map(([rowKey, rowRange], rowIndex) => rowIndex === index ? [key, range] : [rowKey, rowRange])));
  };
  const add = () => {
    let index = rows.length + 1;
    let key = index === 1 ? 'direct' : `router_${index}`;
    while (value[key]) { index += 1; key = `router_${index}`; }
    onChange({ ...value, [key]: { min: 1, max: null } });
  };
  return (
    <div className="rh-provider-editor">
      <div className="rh-smart-field-head">
        <div className="rh-field-label"><IconRoute size={16} /><strong>Trade providers</strong><HelpTip title="Trade providers" text="Require swaps through direct execution or a specific router. Every configured provider is combined with AND." /></div>
        <button className="rh-example-link" type="button" onClick={add}><IconPlus size={13} /> Add</button>
      </div>
      {rows.length === 0 ? <button className="rh-provider-empty" type="button" onClick={add}>No provider rules · add one</button> : (
        <div className="rh-provider-rows">
          {rows.map(([key, range], index) => (
            <div className="rh-provider-row" key={index}>
              <input value={key} maxLength={128} onChange={(event) => update(index, event.currentTarget.value, range)} aria-label="Provider key" />
              <input type="number" step="1" placeholder="Min" value={range.min ?? ''} onChange={(event) => update(index, key, { ...range, min: readNumber(event.currentTarget.value) })} aria-label={`${key} minimum`} />
              <input type="number" step="1" placeholder="Max" value={range.max ?? ''} onChange={(event) => update(index, key, { ...range, max: readNumber(event.currentTarget.value) })} aria-label={`${key} maximum`} />
              <button type="button" onClick={() => onChange(Object.fromEntries(rows.filter((_, rowIndex) => rowIndex !== index)))} aria-label={`Remove ${key}`}><IconX size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExampleModal({ mode, period, onClose }: { mode: 'period' | 'activity' | 'wallet'; period: RobinhoodPeriod; onClose: () => void }) {
  const copy = mode === 'period' ? {
    title: 'How the activity period works',
    body: `A ${period}-day period selects tokens whose last activity falls inside the window before now. Each selected token keeps its lifetime PNL and ROI.`,
    left: `${period}d ago`, right: 'Now', center: 'tokens included',
  } : mode === 'activity' ? {
    title: 'How last activity age works',
    body: '“At least” excludes wallets that traded too recently. “At most” excludes wallets that have been inactive too long. Set both to create an age window.',
    left: 'At most', right: 'At least', center: 'last swap allowed',
  } : {
    title: 'How wallet age works',
    body: 'Wallet age is measured from the first observed swap. “At least 30d” keeps wallets that are 30 days old or older; add “At most” to exclude very old wallets.',
    left: 'Older', right: 'Newer', center: 'first swap allowed',
  };
  return (
    <div className="rh-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="rh-example-modal" role="dialog" aria-modal="true" aria-label={copy.title} onMouseDown={(event) => event.stopPropagation()}>
        <button className="rh-modal-close" type="button" onClick={onClose}><IconX size={18} /></button>
        <div className="rh-modal-icon"><IconClock size={22} /></div>
        <h2>{copy.title}</h2>
        <p>{copy.body}</p>
        <div className="rh-timeline-demo">
          <div className="rh-timeline-labels"><span>{copy.left}</span><span>{copy.right}</span></div>
          <div className="rh-timeline-axis"><i /><b /><em /></div>
          <strong>{copy.center}</strong>
        </div>
        <div className="rh-modal-note"><IconInfoCircle size={16} /><span>Time values accept compact notation: <b>1d2h30m</b>, <b>12h</b> or raw seconds.</span></div>
      </div>
    </div>
  );
}

export const RobinhoodPresets: React.FC = () => {
  const [presets, setPresets] = useState<RobinhoodPreset[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<RobinhoodPreset | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['token-edge', 'activity']));
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [example, setExample] = useState<'period' | 'activity' | 'wallet' | null>(null);
  const [exportSeed, setExportSeed] = useState<RobinhoodExportCreated | null>(null);
  const [exportTask, setExportTask] = useState<RobinhoodExportTask | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [winrateSpectrum, setWinrateSpectrum] = useState<RobinhoodWinrateBucket[] | null>(null);
  const [winrateSpectrumLoading, setWinrateSpectrumLoading] = useState(true);
  const [winrateSpectrumError, setWinrateSpectrumError] = useState<string | null>(null);
  const winrateSpectrumCache = useRef(new Map<RobinhoodPeriod, RobinhoodWinrateBucket[]>());

  const selectedSavedPreset = useMemo(() => presets.find((preset) => preset.id === selectedId) || null, [presets, selectedId]);
  const isDirty = Boolean(draft && selectedSavedPreset && JSON.stringify(draft) !== JSON.stringify(selectedSavedPreset));

  useEffect(() => {
    const telegramWindow = window as Window & { Telegram?: { WebApp?: { expand?: () => void } } };
    telegramWindow.Telegram?.WebApp?.expand?.();
    let mounted = true;
    robinhoodPresetsClient.getPresets()
      .then((data) => {
        if (!mounted) return;
        const normalized = data.map(normalizePreset);
        const selected = normalized.find((preset) => preset.is_active_preset) || normalized[0] || null;
        setPresets(normalized);
        setSelectedId(selected?.id || null);
        setDraft(selected ? clonePreset(selected) : null);
      })
      .catch((error: Error) => mounted && setNotice({ tone: 'error', text: error.message }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const period = draft?.period_days;
    if (!period) return;
    const controller = new AbortController();
    let canceled = false;
    const hasCachedSpectrum = winrateSpectrumCache.current.has(period);
    const cachedSpectrum = winrateSpectrumCache.current.get(period);
    if (hasCachedSpectrum) setWinrateSpectrum(cachedSpectrum || []);
    else setWinrateSpectrum(null);
    setWinrateSpectrumLoading(!hasCachedSpectrum);
    setWinrateSpectrumError(null);
    robinhoodPresetsClient.getWinrateSpectrum(period, controller.signal)
      .then((data) => {
        if (canceled) return;
        const buckets = Array.isArray(data) ? data
          .filter((bucket) => Number.isInteger(bucket?.winrate_percent)
            && bucket.winrate_percent >= 0 && bucket.winrate_percent <= 100
            && Number.isFinite(bucket?.wallets_count) && bucket.wallets_count >= 0
            && Number.isFinite(bucket?.percent) && bucket.percent >= 0)
          .sort((left, right) => left.winrate_percent - right.winrate_percent) : [];
        winrateSpectrumCache.current.set(period, buckets);
        setWinrateSpectrum(buckets);
        setWinrateSpectrumError(null);
        setWinrateSpectrumLoading(false);
      })
      .catch((error) => {
        if (canceled || (error instanceof Error && error.name === 'AbortError')) return;
        if (hasCachedSpectrum) {
          setWinrateSpectrumLoading(false);
          return;
        }
        setWinrateSpectrum(null);
        setWinrateSpectrumError(error instanceof Error ? error.message : 'Could not load the winrate spectrum.');
        setWinrateSpectrumLoading(false);
      });
    return () => { canceled = true; controller.abort(); };
  }, [draft?.period_days]);

  useEffect(() => {
    if (!activeTaskId) return;
    let canceled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const task = await robinhoodPresetsClient.getExportTask(activeTaskId);
        if (canceled) return;
        setExportTask(task);
        if (!TERMINAL_EXPORT_STATUSES.has(task.status)) timer = setTimeout(poll, 4000);
      } catch (error) {
        if (!canceled) setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not refresh export status.' });
      }
    };
    timer = setTimeout(poll, 1200);
    return () => { canceled = true; if (timer) clearTimeout(timer); };
  }, [activeTaskId]);

  const updateTopLevel = <K extends keyof RobinhoodPreset>(key: K, value: RobinhoodPreset[K]) => setDraft((current) => current ? { ...current, [key]: value } : current);
  const updateFilter = (key: RobinhoodScalarFilterKey, value: number | null) => setDraft((current) => current ? { ...current, filters: { ...current.filters, [key]: value } } : current);
  const updateProviders = (value: RobinhoodMetricMap) => setDraft((current) => current ? { ...current, filters: { ...current.filters, trade_providers: value } } : current);
  const updateBalanceFilters = (patch: Partial<BalanceFilters>) => setDraft((current) => current ? {
    ...current,
    filters: { ...current.filters, ...patch },
  } : current);

  const selectPreset = (id: number) => {
    const preset = presets.find((item) => item.id === id);
    if (!preset) return;
    setSelectedId(id);
    setDraft(clonePreset(preset));
    setDeleteArmed(false);
    setNotice(null);
  };

  const allVisibleRanges = [...PERFORMANCE_RANGES, ...FILTER_SECTIONS.flatMap((section) => section.ranges), ...DURATION_RANGES];
  const validate = (preset: RobinhoodPreset) => {
    const errors: string[] = [];
    const name = preset.name.trim();
    if (name.length < 3 || name.length > 32 || !/^[\w\u0400-\u04FF -]+$/.test(name)) errors.push('Preset name must be 3–32 characters and use letters, numbers, spaces, _ or -.');
    if (!Number.isInteger(preset.tokens_per_wallet) || preset.tokens_per_wallet < 1 || preset.tokens_per_wallet > 1000) errors.push('Tokens per wallet must be an integer from 1 to 1000.');
    allVisibleRanges.forEach((range) => {
      const min = preset.filters[range.min];
      const max = preset.filters[range.max];
      if ((min != null && !Number.isFinite(min)) || (max != null && !Number.isFinite(max))) {
        errors.push(`${range.label}: values must be finite numbers.`);
      }
      if (range.minimum != null && ((min != null && min < range.minimum) || (max != null && max < range.minimum))) {
        errors.push(`${range.label}: values cannot be negative.`);
      }
      if (min != null && max != null && min > max) errors.push(`${range.label}: minimum cannot be greater than maximum.`);
    });
    const lastMin = preset.filters.last_activity_min_age_seconds;
    const lastMax = preset.filters.last_activity_max_age_seconds;
    if (lastMin != null && lastMax != null && lastMin > lastMax) errors.push('Last activity: “at least” cannot exceed “at most”.');
    const walletMin = preset.filters.wallet_age_min_seconds;
    const walletMax = preset.filters.wallet_age_max_seconds;
    if (walletMin != null && walletMax != null && walletMin > walletMax) errors.push('Wallet age: “at least” cannot exceed “at most”.');
    ([
      ['WETH', preset.filters.weth_balance_min, preset.filters.weth_balance_max, BUILT_IN_BALANCE_PRECISION],
      ['ETH', preset.filters.eth_balance_min, preset.filters.eth_balance_max, BUILT_IN_BALANCE_PRECISION],
      ['Total ETH', preset.filters.total_eth_balance_min, preset.filters.total_eth_balance_max, BUILT_IN_BALANCE_PRECISION],
      ['USDG', preset.filters.usdg_balance_min, preset.filters.usdg_balance_max, BUILT_IN_BALANCE_PRECISION],
    ] as Array<[string, string | null, string | null, number]>).forEach(([token, min, max, decimals]) => {
      if (min != null && !isDecimalBalance(min, decimals)) errors.push(`${token}: minimum must be a non-negative decimal value.`);
      if (max != null && !isDecimalBalance(max, decimals)) errors.push(`${token}: maximum must be a non-negative decimal value.`);
      if (min != null && max != null && isDecimalBalance(min, decimals) && isDecimalBalance(max, decimals) && compareDecimalBalances(min, max) > 0) {
        errors.push(`${token}: minimum cannot exceed maximum.`);
      }
    });
    const customContracts = new Set<string>();
    (preset.filters.custom_balance || []).forEach((item, index) => {
      const label = `Custom token ${index + 1}`;
      const contract = item.contract.toLowerCase();
      if (!/^0x[a-f0-9]{40}$/.test(contract)) errors.push(`${label}: contract must be a valid 0x address.`);
      if (customContracts.has(contract)) errors.push(`${label}: duplicate contract.`);
      customContracts.add(contract);
      if (item.min != null && !isRawUInt256(item.min)) errors.push(`${label}: minimum must be a raw UInt256 string.`);
      if (item.max != null && !isRawUInt256(item.max)) errors.push(`${label}: maximum must be a raw UInt256 string.`);
      if (item.min != null && item.max != null && isRawUInt256(item.min) && isRawUInt256(item.max) && compareRawAmounts(item.min, item.max) > 0) {
        errors.push(`${label}: minimum cannot exceed maximum.`);
      }
    });
    Object.entries(preset.filters.trade_providers).forEach(([key, range]) => {
      if (!/^[A-Za-z0-9_.:/-]{1,128}$/.test(key)) errors.push(`Trade provider “${key}” contains unsupported characters.`);
      if (range.min != null && range.max != null && range.min > range.max) errors.push(`Trade provider ${key}: minimum cannot exceed maximum.`);
    });
    return errors;
  };

  const applySavedPreset = (saved: RobinhoodPreset) => {
    const normalized = normalizePreset(saved);
    setPresets((current) => current.map((preset) => preset.id === normalized.id ? normalized : normalized.is_active_preset ? { ...preset, is_active_preset: false } : preset));
    setDraft(clonePreset(normalized));
    setSelectedId(normalized.id);
  };

  const savePreset = async (preset: RobinhoodPreset) => {
    const errors = validate(preset);
    if (errors.length) throw new Error(errors[0]);
    return robinhoodPresetsClient.updatePreset(preset.id, { ...toPayload(preset), name: preset.name.trim() });
  };

  const handleSave = async () => {
    if (!draft) return;
    setAction('save'); setNotice(null);
    try {
      applySavedPreset(await savePreset(draft));
      setNotice({ tone: 'success', text: 'Preset saved.' });
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not save preset.' });
    } finally { setAction(null); }
  };

  const handleActivate = async () => {
    if (!draft || draft.is_active_preset) return;
    setAction('activate'); setNotice(null);
    try {
      const saved = await savePreset({ ...draft, is_active_preset: true });
      applySavedPreset(saved);
      setNotice({ tone: 'success', text: 'This preset is now active for Robinhood files.' });
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not activate preset.' });
    } finally { setAction(null); }
  };

  const handleCreate = async () => {
    if (presets.length >= 10) { setNotice({ tone: 'error', text: 'You already have the maximum of 10 presets.' }); return; }
    const names = new Set(presets.map((preset) => preset.name.toLowerCase()));
    let index = 1; while (names.has(`preset ${index}`)) index += 1;
    setAction('create');
    try {
      const created = normalizePreset(await robinhoodPresetsClient.createPreset({ name: `Preset ${index}` }));
      setPresets((current) => [...current, created]); setSelectedId(created.id); setDraft(clonePreset(created));
      setNotice({ tone: 'success', text: 'New preset created.' });
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not create preset.' }); }
    finally { setAction(null); }
  };

  const makeCopyName = (name: string) => {
    const names = new Set(presets.map((preset) => preset.name.toLowerCase()));
    let index = 1; let candidate = `${name.slice(0, 25)} copy`;
    while (names.has(candidate.toLowerCase())) { index += 1; candidate = `${name.slice(0, 23)} copy ${index}`; }
    return candidate.slice(0, 32);
  };

  const handleCopy = async () => {
    if (!draft || presets.length >= 10) { setNotice({ tone: 'error', text: 'Preset cannot be copied: the 10 preset limit is reached.' }); return; }
    setAction('copy');
    try {
      const created = normalizePreset(await robinhoodPresetsClient.createPreset({ ...toPayload(draft), name: makeCopyName(draft.name), is_active_preset: false }));
      setPresets((current) => [...current, created]); setSelectedId(created.id); setDraft(clonePreset(created));
      setNotice({ tone: 'success', text: 'Preset copied.' });
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not copy preset.' }); }
    finally { setAction(null); }
  };

  const handleDelete = async () => {
    if (!draft || draft.name.toLowerCase() === 'base') return;
    if (!deleteArmed) { setDeleteArmed(true); return; }
    setAction('delete');
    try {
      await robinhoodPresetsClient.deletePreset(draft.id);
      const remaining = presets.filter((preset) => preset.id !== draft.id);
      const next = remaining.find((preset) => preset.is_active_preset) || remaining[0] || null;
      setPresets(remaining); setSelectedId(next?.id || null); setDraft(next ? clonePreset(next) : null); setDeleteArmed(false);
      setNotice({ tone: 'success', text: 'Preset deleted.' });
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not delete preset.' }); }
    finally { setAction(null); }
  };

  const handleExport = async () => {
    if (!draft) return;
    setAction('export'); setNotice(null); setExportSeed(null); setExportTask(null); setActiveTaskId(null);
    try {
      let exportPreset = draft;
      if (isDirty) { exportPreset = await savePreset(draft); applySavedPreset(exportPreset); }
      const created = await robinhoodPresetsClient.exportWallets(exportPreset.id);
      setExportSeed(created); setActiveTaskId(created.task_id);
      setNotice({ tone: 'info', text: created.queued_for_release ? 'Export is queued until the release finishes.' : 'Export started. Status will update here.' });
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not start export.' }); }
    finally { setAction(null); }
  };

  const clearAllFilters = () => setDraft((current) => current ? {
    ...current,
    filters: {
      ...current.filters,
      ...Object.fromEntries(ROBINHOOD_SCALAR_FILTER_KEYS.map((key) => [key, null])),
      trade_providers: {}, quote_tokens: {}, projects: {},
      weth_balance_min: null, weth_balance_max: null,
      eth_balance_min: null, eth_balance_max: null,
      total_eth_balance_min: null, total_eth_balance_max: null,
      usdg_balance_min: null, usdg_balance_max: null,
      custom_balance: [], balance_mode_or: true,
    },
  } : current);

  const rangeIsActive = (range: RangeDefinition) => Boolean(draft && (draft.filters[range.min] != null || draft.filters[range.max] != null));
  const sectionCount = (section: FilterSectionDefinition) => section.ranges.reduce((count, range) => count + Number(rangeIsActive(range)), 0);
  const filterCount = draft ? [
    Number(draft.filters.min_winrate != null || draft.filters.max_winrate != null),
    Number(draft.filters.last_activity_min_age_seconds != null || draft.filters.last_activity_max_age_seconds != null),
    Number(draft.filters.wallet_age_min_seconds != null || draft.filters.wallet_age_max_seconds != null),
    Number(draft.filters.min_rocket_x2_count != null || draft.filters.max_rocket_x2_count != null),
    Number(draft.filters.min_rocket_x5_count != null || draft.filters.max_rocket_x5_count != null),
    Number(draft.filters.min_rocket_x10_count != null || draft.filters.max_rocket_x10_count != null),
    Number(draft.filters.weth_balance_min != null || draft.filters.weth_balance_max != null),
    Number(draft.filters.eth_balance_min != null || draft.filters.eth_balance_max != null),
    Number(draft.filters.total_eth_balance_min != null || draft.filters.total_eth_balance_max != null),
    Number(draft.filters.usdg_balance_min != null || draft.filters.usdg_balance_max != null),
    ...(draft.filters.custom_balance || []).map((item) => Number(item.min != null || item.max != null)),
    ...allVisibleRanges.map((range) => Number(rangeIsActive(range))),
    Object.keys(draft.filters.trade_providers).length,
  ].reduce((total, count) => total + count, 0) : 0;
  const taskStatus = exportTask?.status || exportSeed?.status;
  const progress = exportTask?.total_rows ? Math.min(100, Math.round((exportTask.processed_rows / exportTask.total_rows) * 100)) : 0;

  if (loading) return <main className="rh-page rh-loading-page"><div className="rh-loader-mark"><IconSparkles size={24} /></div><p>Loading Robinhood presets…</p></main>;

  return (
    <main className="rh-page">
      <div className="rh-page-glow" /><div className="rh-noise" />
      <div className="rh-shell">
        <header className="rh-hero">
          <div className="rh-brand-mark"><IconChartBar size={24} /></div>
          <div><div className="rh-eyebrow"><span /> EVM WALLET ANALYTICS</div><h1>Robinhood <em>Deep</em></h1></div>
          <div className="rh-network-pill"><i /> LIVE</div>
        </header>

        {notice && <div className={`rh-notice rh-notice-${notice.tone}`} role="status"><IconInfoCircle size={17} /><span>{notice.text}</span><button type="button" onClick={() => setNotice(null)}><IconX size={15} /></button></div>}

        <section className="rh-preset-toolbar rh-glass-card">
          <div className="rh-preset-line">
            <div className="rh-preset-choice">
              <div className="rh-select-shell rh-preset-select">
                <select value={selectedId ?? ''} onChange={(event) => selectPreset(Number(event.currentTarget.value))} aria-label="Preset">
                  {presets.map((preset) => <option value={preset.id} key={preset.id}>{preset.is_active_preset ? '● ' : ''}{preset.name}</option>)}
                </select><IconChevronDown size={16} />
              </div>
              {draft?.is_active_preset
                ? <div className="rh-active-preset"><i /><span>Active</span></div>
                : draft && <button className="rh-activate-button" type="button" onClick={handleActivate} disabled={Boolean(action)}><IconCheck size={15} /><span>Make active</span></button>}
            </div>
            <div className="rh-preset-actions">
              <button type="button" title="New preset" onClick={handleCreate} disabled={Boolean(action) || presets.length >= 10}><IconPlus size={16} /><span>New</span></button>
              <button type="button" title="Copy preset" onClick={handleCopy} disabled={!draft || Boolean(action) || presets.length >= 10}><IconCopy size={15} /><span>Copy</span></button>
              <button type="button" title="Delete preset" className={deleteArmed ? 'danger' : ''} onClick={handleDelete} onBlur={() => setDeleteArmed(false)} disabled={!draft || draft.name.toLowerCase() === 'base' || Boolean(action)}><IconTrash size={15} /><span>{deleteArmed ? 'Sure?' : 'Delete'}</span></button>
            </div>
          </div>
          {draft && (
            <>
              <div className="rh-preset-editor-row">
                <label className="rh-preset-name-field"><span>PRESET NAME</span><input value={draft.name} maxLength={32} onChange={(event) => updateTopLevel('name', event.currentTarget.value)} /></label>
                <div className="rh-preset-cta">
                  <div className="rh-export-wrap">
                    <span className="rh-export-spark one">+$</span><span className="rh-export-spark two">↗</span><span className="rh-export-spark three">$</span>
                    <span className="rh-export-spark four">%</span><span className="rh-export-spark five">▲</span><span className="rh-export-spark six">+</span>
                    <button
                      className="rh-export-button"
                      type="button"
                      title="Full wallet export is available in the DAO version. Free users can analyze a TXT wallet list through the bot."
                      onClick={handleExport}
                      disabled={Boolean(action)}
                    >
                      {action === 'export' ? <span className="rh-button-spinner" /> : <IconDownload size={18} />}
                      {action === 'export' ? 'Starting…' : 'Export wallets'}
                      <small>DAO</small>
                    </button>
                  </div>
                  <button className="rh-view-settings-button" type="button" onClick={() => setSettingsOpen(true)}><IconAdjustments size={15} /><span>View settings</span></button>
                </div>
              </div>

              {taskStatus && (
                <div className={`rh-export-status ${TERMINAL_EXPORT_STATUSES.has(taskStatus) ? `is-${taskStatus}` : ''}`}>
                  <div><i /><strong>{friendlyStatus(taskStatus)}</strong><span>{exportSeed?.task_id}</span></div>
                  {exportTask && !TERMINAL_EXPORT_STATUSES.has(exportTask.status) && <div className="rh-progress"><i style={{ width: `${progress}%` }} /></div>}
                  {exportTask?.status === 'done' && <span>{exportTask.matched_rows.toLocaleString()} wallets matched · sent to Telegram</span>}
                  {exportTask?.status === 'error' && <span>Try again later or contact support.</span>}
                </div>
              )}
            </>
          )}
        </section>

        {draft ? (
          <>
            <section className="rh-setup-card rh-glass-card">
              <div className="rh-config-grid">
                <div className="rh-format-control">
                  <div className="rh-control-title"><span>FILE FORMAT</span></div>
                  <FormatPicker value={draft.format_type} onChange={(value) => updateTopLevel('format_type', value)} />
                </div>
                <div className="rh-period-control">
                  <div className="rh-control-title"><span>ACTIVITY PERIOD</span><HelpTip title="Activity period" text="The period selects tokens by calendar age of their last activity. PNL and ROI for those tokens remain lifetime metrics." /><button type="button" onClick={() => setExample('period')}>Show example</button></div>
                  <div className="rh-period-segments">{PERIODS.map((period) => <button type="button" aria-label={`${period}d`} key={period} className={draft.period_days === period ? 'active' : ''} onClick={() => updateTopLevel('period_days', period)}>{period}<span>d</span></button>)}</div>
                </div>
              </div>
              <div className="rh-setup-footer">
                <Toggle checked={draft.is_active} onChange={(value) => updateTopLevel('is_active', value)} label="Apply filters" detail="Turn off to export the period without metric filters" />
                <div className="rh-filter-summary"><span className="rh-filter-count"><strong>{filterCount}</strong><span>{filterCount === 1 ? 'active filter' : 'active filters'}</span></span>{filterCount > 0 && <button type="button" onClick={clearAllFilters}>Clear all</button>}</div>
              </div>
            </section>

            {draft.is_active ? (
              <section className="rh-filter-workspace">
                <div className="rh-section-intro"><div><div className="rh-eyebrow"><span /> CORE SIGNAL</div><h2>Start with the outcome</h2><p>Only the essentials are open. Add limits when they help; leave the rest empty.</p></div></div>
                <div className="rh-performance-card rh-glass-card">
                  <WinrateControl filters={draft.filters} onChange={updateFilter} spectrum={winrateSpectrum} spectrumLoading={winrateSpectrumLoading} spectrumError={winrateSpectrumError} period={draft.period_days} />
                </div>
                <div className="rh-balance-card rh-glass-card">
                  <BalanceFiltersControl filters={draft.filters} onChange={updateBalanceFilters} />
                </div>
                <div className="rh-primary-ranges rh-core-ranges-card rh-glass-card">
                  {PERFORMANCE_RANGES.map((range) => <RangeField key={range.min} definition={range} filters={draft.filters} onChange={updateFilter} />)}
                </div>

                <div className="rh-section-intro rh-time-intro"><div><div className="rh-eyebrow"><span /> TIME WINDOWS</div><h2>Recency without raw seconds</h2></div></div>
                <div className="rh-time-card rh-glass-card">
                  <DurationWindow
                    title="Last activity" help="Time since the wallet's last swap. Use both values to keep wallets that traded inside a specific age window."
                    min={draft.filters.last_activity_min_age_seconds} max={draft.filters.last_activity_max_age_seconds}
                    onMinChange={(value) => updateFilter('last_activity_min_age_seconds', value)} onMaxChange={(value) => updateFilter('last_activity_max_age_seconds', value)}
                    onExample={() => setExample('activity')}
                    presets={[{ label: 'Within 24h', min: null, max: 86400 }, { label: '1–7 days', min: 86400, max: 604800 }, { label: '7–30 days', min: 604800, max: 2592000 }]}
                  />
                  <DurationWindow
                    title="Wallet age" help="Time since the first observed swap. A minimum age removes freshly created wallets."
                    min={draft.filters.wallet_age_min_seconds} max={draft.filters.wallet_age_max_seconds}
                    onMinChange={(value) => updateFilter('wallet_age_min_seconds', value)} onMaxChange={(value) => updateFilter('wallet_age_max_seconds', value)}
                    onExample={() => setExample('wallet')}
                    presets={[{ label: '7d+', min: 604800, max: null }, { label: '30d+', min: 2592000, max: null }, { label: '30–180d', min: 2592000, max: 15552000 }]}
                  />
                </div>

                <div className="rh-accordion-list">
                  {FILTER_SECTIONS.map((section) => {
                    const SectionIcon = section.icon;
                    const open = openSections.has(section.id);
                    const count = sectionCount(section);
                    return (
                      <article className={`rh-filter-card ${open ? 'open' : ''}`} key={section.id}>
                        <button className="rh-filter-header" type="button" onClick={() => setOpenSections((current) => { const next = new Set(current); next.has(section.id) ? next.delete(section.id) : next.add(section.id); return next; })}>
                          <span className="rh-filter-icon"><SectionIcon className="rh-filter-vector" size={18} /><span className="rh-filter-emoji">{section.emoji}</span></span><span><strong>{section.title}</strong><small>{section.description}</small></span>{count > 0 && <b>{count}</b>}<IconChevronDown className="rh-chevron" size={18} />
                        </button>
                        {open && <div className="rh-filter-body">{section.ranges.map((range) => <RangeField key={range.min} definition={range} filters={draft.filters} onChange={updateFilter} />)}{section.id === 'quality' && <TradeProvidersEditor value={draft.filters.trade_providers} onChange={updateProviders} />}</div>}
                      </article>
                    );
                  })}

                  <article className={`rh-filter-card ${openSections.has('timing') ? 'open' : ''}`}>
                    <button className="rh-filter-header" type="button" onClick={() => setOpenSections((current) => { const next = new Set(current); next.has('timing') ? next.delete('timing') : next.add('timing'); return next; })}>
                      <span className="rh-filter-icon"><IconClock className="rh-filter-vector" size={18} /><span className="rh-filter-emoji">⏱️</span></span><span><strong>Trade timing</strong><small>Human-readable holding and exit durations.</small></span><IconChevronDown className="rh-chevron" size={18} />
                    </button>
                    {openSections.has('timing') && <div className="rh-filter-body rh-duration-stack">{DURATION_RANGES.map((range) => <DurationWindow key={range.min} title={range.label} help={range.help} min={draft.filters[range.min]} max={draft.filters[range.max]} onMinChange={(value) => updateFilter(range.min, value)} onMaxChange={(value) => updateFilter(range.max, value)} presets={[{ label: '1m–1h', min: 60, max: 3600 }, { label: '1h–1d', min: 3600, max: 86400 }, { label: '1–7 days', min: 86400, max: 604800 }]} />)}</div>}
                  </article>

                  <article className={`rh-filter-card ${openSections.has('rockets') ? 'open' : ''}`}>
                    <button className="rh-filter-header" type="button" onClick={() => setOpenSections((current) => { const next = new Set(current); next.has('rockets') ? next.delete('rockets') : next.add('rockets'); return next; })}>
                      <span className="rh-filter-icon"><IconRocket className="rh-filter-vector" size={18} /><span className="rh-filter-emoji">🚀</span></span><span><strong>Rockets</strong><small>Count repeat ×2, ×5 and ×10 outcomes.</small></span><IconChevronDown className="rh-chevron" size={18} />
                    </button>
                    {openSections.has('rockets') && <div className="rh-filter-body"><RocketsControl filters={draft.filters} onChange={updateFilter} /></div>}
                  </article>
                </div>
              </section>
            ) : <div className="rh-filters-off"><IconBan size={19} /><div><strong>Metric filters are paused</strong><span>The period and output settings will still be used.</span></div></div>}

            <div className="rh-save-dock-spacer" />
            <div className="rh-save-dock"><div className="rh-save-state"><i className={isDirty ? 'dirty' : ''} /><span>{isDirty ? 'Unsaved changes' : 'All changes saved'}</span></div><button type="button" onClick={handleSave} disabled={!isDirty || Boolean(action)}>{action === 'save' ? <span className="rh-button-spinner" /> : <IconDeviceFloppy size={17} />}{action === 'save' ? 'Saving…' : 'Save preset'}</button></div>

            {settingsOpen && (
              <div className="rh-drawer-backdrop" onMouseDown={() => setSettingsOpen(false)}>
                <aside className="rh-settings-drawer" role="dialog" aria-modal="true" aria-label="Output view settings" onMouseDown={(event) => event.stopPropagation()}>
                  <div className="rh-drawer-head"><div><div className="rh-eyebrow"><span /> OUTPUT ONLY</div><h2>View settings</h2><p>These options change the files, not which wallets match.</p></div><button type="button" onClick={() => setSettingsOpen(false)}><IconX size={19} /></button></div>
                  <div className="rh-drawer-section">
                    <h3>Token details</h3>
                    <Toggle checked={draft.include_token_details} onChange={(value) => updateTopLevel('include_token_details', value)} label="Include Tokens sheets" detail="Available for Excel exports" />
                    <label className="rh-drawer-field"><span>Tokens per wallet</span><input type="number" min="1" max="1000" step="1" value={draft.tokens_per_wallet} onChange={(event) => updateTopLevel('tokens_per_wallet', Number(event.currentTarget.value))} /><small>1–1000; large exports may be reduced evenly.</small></label>
                  </div>
                  <div className="rh-drawer-section">
                    <h3>Wallet summary</h3>
                    <label className="rh-drawer-field"><span>Sort by</span><div className="rh-select-shell"><select value={draft.summary_order_column} onChange={(event) => updateTopLevel('summary_order_column', event.currentTarget.value)}>{SUMMARY_SORT_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select><IconChevronDown size={16} /></div></label>
                    <Toggle checked={draft.summary_order_desc} onChange={(value) => updateTopLevel('summary_order_desc', value)} label="Descending order" detail="Highest values first" />
                  </div>
                  <div className="rh-drawer-section">
                    <h3>Token sheets</h3>
                    <label className="rh-drawer-field"><span>Sort by</span><div className="rh-select-shell"><select value={draft.token_order_column} onChange={(event) => updateTopLevel('token_order_column', event.currentTarget.value)}>{TOKEN_SORT_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select><IconChevronDown size={16} /></div></label>
                    <Toggle checked={draft.token_order_desc} onChange={(value) => updateTopLevel('token_order_desc', value)} label="Descending order" detail="Highest values first" />
                  </div>
                  <button className="rh-drawer-done" type="button" onClick={() => setSettingsOpen(false)}><IconCheck size={17} /> Done</button>
                </aside>
              </div>
            )}

            {example && <ExampleModal mode={example} period={draft.period_days} onClose={() => setExample(null)} />}
          </>
        ) : <section className="rh-empty-state rh-glass-card"><IconWallet size={28} /><h2>No presets available</h2><button type="button" onClick={handleCreate}><IconPlus size={16} /> Create preset</button></section>}
      </div>
    </main>
  );
};

export default RobinhoodPresets;
